import { useEffect, useRef, useCallback } from 'react';

const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutos
const CHECK_INTERVAL_MS = 60 * 1000; // 1 minuto

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

/**
 * Hook que detecta inactividad y ejecuta logout tras 15 minutos sin actividad.
 * @param {boolean} enabled - Si true, activa el monitoreo
 * @param {Function} onLogout - Callback a ejecutar al cerrar sesión por inactividad
 */
export function useInactivityLogout(enabled, onLogout) {
  const lastActivityRef = useRef(Date.now());
  const checkIntervalRef = useRef(null);

  const onLogoutRef = useRef(onLogout);
  onLogoutRef.current = onLogout;

  const handleActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const checkInactivity = () => {
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= INACTIVITY_TIMEOUT_MS) {
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
          checkIntervalRef.current = null;
        }
        ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, handleActivity));
        onLogoutRef.current?.();
      }
    };

    lastActivityRef.current = Date.now();
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, handleActivity));
    checkIntervalRef.current = setInterval(checkInactivity, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, handleActivity));
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [enabled, handleActivity]);
}

export { INACTIVITY_TIMEOUT_MS };
